/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

/* eslint-disable security/detect-object-injection */
/* eslint-disable security/detect-non-literal-fs-filename */
import * as MsDyn365 from '@msdyn365-commerce/core';
import {
    ProductDeliveryOptions,
    ProductSearchResult,
    ProductWarehouseInventoryAvailability,
    ProductWarehouseInventoryInformation
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getProductPageUrlSync, IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
// import { ProductComponent } from '@msdyn365-commerce/components';
import { ProductComponent } from '../../themes/dobbies/views/components/product.component';
//import { getCartFromCustomer } from '@msdyn365-commerce/global-state';
import {
    getActivePricesAsync,
    getEstimatedAvailabilityAsync,
    getVariantsByDimensionValuesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    isMobile,
    ITelemetryContent,
    VariantType,
    Node,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    activeImageAdditionalContent,
    ActiveProductComponent,
    ActiveProductsCarousel,
    IArea,
    IAreaMap,
    ImageMapper
} from './active-image-utilities';
import { IDobbiesProductCarouselData } from './dobbies-product-carousel.data';
import { IDobbiesProductCarouselProps } from './dobbies-product-carousel.props.autogenerated';
import { ProductPrice, ProjectionDomain, DiscountLine } from '@msdyn365-commerce/retail-proxy';
import { reaction } from 'mobx';
import { IProductWithModes } from '../dobbies-search-result-container/components';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getSearchByCriteriaAsync, GetSearchByCriteriaInput } from '../../themes/dobbies/actions/search-by-criteria';
import { mapProductInventoryInformation } from '../../themes/dobbies/utilities';

/**
 * @type AreaEvent for active points
 */
type AreaEvent = Event | React.MouseEvent<HTMLAreaElement, MouseEvent> | React.FocusEvent<HTMLAreaElement>;

/**
 * @interface IActiveImageViewProps
 * @classdesc - The IActiveImageViewProps interface contract.
 */
export interface IActiveImageViewProps extends IDobbiesProductCarouselProps<{}> {
    featuredProducts: React.ReactNode;
    activePointContainer: IModuleProps;
    activePointImageContainer: INodeProps;
    activePointDetailsContainer: INodeProps;
    imageMapper: React.ReactNode;
    imgWidth?: number;
    productCard: React.ReactNode;
    title?: React.ReactNode;
    text?: React.ReactNode;
    links?: React.ReactNode;
    detailsContainer?: INodeProps;
    detailsContainerCta?: INodeProps;
    additionalContent?: React.ReactNode;
    handleAdditionalText?(event: MsDyn365.ContentEditableEvent): void;
    handleAdditionalParagraph?(event: MsDyn365.ContentEditableEvent): void;
    handleAdditionalLinkText?(event: MsDyn365.ContentEditableEvent): void;
}

/**
 * @interface IActiveImageLinksData
 * @classdesc - The IActiveImageLinksData interface contract.
 */
export interface IActiveImageLinksData {
    linkText?: string;
    linkUrl: MsDyn365.ILinkData;
    ariaLabel?: string;
    willOpenInNewTab?: boolean;
}

export const enum DeviceType {
    Desktop,
    Mobile,
    Tablet
}

/**
 * @interface IActiveImageState
 * @classdesc - The IActiveImageState interface contract.
 */
interface IActiveImageState {
    clientWidth: number;
    index?: number;
    area?: IArea;
    viewport: string;
    device: DeviceType;
    discountLines: (DiscountLine[] | undefined)[];
    products: ProductSearchResult[];
    nonClubPlusDiscount: ProductPrice[];
    inventoryInfo: IProductInventoryInformation[];
    cncInventoryInfo?: ProductWarehouseInventoryInformation;
    deliveryOptions: ProductDeliveryOptions[];
    isLoading: boolean;
}

/**
 * @enum ActiveImageConstants
 * @description - The active image constants enum.
 */
export enum ActiveImageConstants {
    defaultPopupWidth = 350,
    backgroundColor = '#f8f8f8',
    fontColor = '#111111',
    totalXPadding = 40,
    cardGap = 10,
    defaultRadius = 5,
    leaveDelay = 150,
    timeoutInterval = 100,
    enterKey = 'Enter',
    escKey = 'Escape'
}

/**
 *
 * ActiveImage class.
 * @extends {React.PureComponent<IDobbiesActiveImageProps<{}>, IActiveImageState>}
 */

export class ActiveImage extends React.PureComponent<IDobbiesProductCarouselProps<IDobbiesProductCarouselData>, IActiveImageState> {
    private readonly _telemetryContent: ITelemetryContent;

    private readonly _defaultProdutCardHight: number = 490;

    private readonly _activePoints: IArea[] = [];

    private readonly _activeImageRefs: React.RefObject<HTMLDivElement>;

    private readonly _activeImageClass: string;

    private readonly _productCardDivRef: React.RefObject<HTMLDivElement>;

    private readonly detailsRef = React.createRef<HTMLDivElement>();

    private readonly overlayRef = React.createRef<HTMLDivElement>();

    private timeout?: NodeJS.Timeout;

    public constructor(props: IDobbiesProductCarouselProps<IDobbiesProductCarouselData>) {
        super(props);
        this._telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        const mobile = this.props.context.request?.device?.Type === 'Mobile';
        const device = mobile || viewport === 'xs' ? DeviceType.Mobile : viewport === 'sm' ? DeviceType.Tablet : DeviceType.Desktop;
        this._activeImageClass = `msc-active-image-${this.getActiveImageId()}`;
        this.state = {
            device,
            clientWidth: 0,
            viewport,
            discountLines: [],
            products: [],
            nonClubPlusDiscount: [],
            inventoryInfo: [],
            deliveryOptions: [],
            isLoading: true
        };
        this._activeImageRefs = React.createRef();
        this._productCardDivRef = React.createRef();
    }

    public componentDidMount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.addEventListener('resize', this.updateClientWidth);
            document.addEventListener('keydown', this.keyDown, false);
            document.addEventListener('mousedown', this._handleOutsideClick);
        }
        setTimeout(this.updateClientWidth, ActiveImageConstants.timeoutInterval);
        if (MsDyn365.msdyn365Commerce.isBrowser && this._activeImageRefs.current) {
            const images: HTMLCollectionOf<Element> = this._activeImageRefs.current.getElementsByClassName(
                'msc-thumbnail_image msc-active-image-img'
            );
            if (ArrayExtensions.hasElements(Array.from(images))) {
                images[0].removeAttribute('usemap');
            }
        }
        reaction(
            () => this.props.data.productsCarousel.result,
            async () => {
                if (this.props.config.enableAffiliationBasedPricing) {
                    void this.updateProductsPrices(this.props.data.productsCarousel.result);
                } else {
                    this.setState({ products: this.props.data.productsCarousel.result ? this.props.data.productsCarousel.result : [] });
                    void this.getNonClubPlusDiscountPrices();
                }
            }
        );
    }

    private readonly getNonClubPlusDiscountPrices = async (): Promise<void> => {
        const products = this.props.data.productsCarousel.result || [];
        const productRecIds = ArrayExtensions.hasElements(products) ? products.map(prod => prod.RecordId) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        await getActivePricesAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            null,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                if (activePricesResponse && activePricesResponse.length > 0) {
                    activePricesResponse = activePricesResponse.filter(prod => {
                        const activePrice = products?.find(price => price.RecordId === prod.ProductId);
                        if (activePrice) {
                            return true;
                        }
                        return false;
                    });
                    this.setState({
                        nonClubPlusDiscount: activePricesResponse
                    });
                    this.setupInventoryInformation(products);
                }
            })
            .catch((error: Error) => {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    '[get Affiliation based price on search result container ]Error executing action'
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
    };

    private updateProductsPrices = async (rawProducts: ProductSearchResult[] | undefined): Promise<void> => {
        const priceAffiliations = [
            {
                AffiliationId: this.props.context.app.config.AffiliationId,
                LoyaltyTierId: this.props.context.app.config.LoyaltyTierId,
                AffiliationTypeValue: this.props.context.app.config.AffiliationTypeValue,
                ReasonCodeLines: [],
                CustomerId: '',
                ExtensionProperties: []
            }
        ];
        const productRecIds = ArrayExtensions.hasElements(rawProducts) ? rawProducts.map(prod => prod.RecordId) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        // const cart = await getCartFromCustomer(this.props.context.actionContext);
        await getActivePricesAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            priceAffiliations,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                const discountLines = activePricesResponse.map(priceResp => priceResp.DiscountLines);
                if (this.state.discountLines.length <= 0 && discountLines) {
                    this.setState({
                        discountLines: discountLines
                    });
                }

                let updatedProducts = rawProducts?.map(prod => {
                    const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
                    if (activePrice) {
                        prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
                        prod.BasePrice = Math.max(
                            activePrice.BasePrice || 0,
                            activePrice.TradeAgreementPrice || 0,
                            activePrice.AdjustedPrice || 0
                        );
                    }
                    return prod;
                });
                updatedProducts = updatedProducts?.filter(prod => {
                    const activePrice = activePricesResponse?.find(price => price.ProductId === prod.RecordId);
                    if (activePrice) {
                        return true;
                    }
                    return false;
                });
                if (updatedProducts) {
                    this.setState({ products: updatedProducts });
                    this.setupInventoryInformation(updatedProducts);
                }
                return;
            })
            .catch((error: Error) => {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    '[get Affiliation based price on search result container ]Error executing action'
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
        if (rawProducts) {
            this.setState({ products: rawProducts });
        }
        return;
    };

    /**
     * AddActivePoints method - To handle add Active Points.
     * @param area - IArea.
     * @param product - ProductSearchResult.
     * @param coords - Coords.
     * @param productUrl - String.
     */
    public addActivePoints = (area: IArea | undefined, product: ProductSearchResult, coords: number[], productUrl: string): void => {
        if (!area) {
            for (const ap of this._activePoints) {
                if (ap.areaId === product.RecordId.toString()) {
                    return;
                }
            }
            this._activePoints.push({
                areaId: product.RecordId.toString(),
                coords,
                primaryImageUrl: product.PrimaryImageUrl ?? '',
                productUrl,
                productSearchResult: product
            });
        }
    };

    /**
     * ComponentWillUnmount method.
     */
    public componentWillUnmount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.removeEventListener('resize', this.updateClientWidth);
            document.removeEventListener('keydown', (this.keyDown as unknown) as EventListener, false);
            document.removeEventListener('mousedown', this._handleOutsideClick, false);
        }
    }

    /**
     * UpdateClientWidth method to update client width in state.
     */
    public updateClientWidth = (): void => {
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        const device = viewport === 'xs' ? DeviceType.Mobile : viewport === 'sm' ? DeviceType.Tablet : DeviceType.Desktop;
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            if (device !== DeviceType.Desktop && this.detailsRef.current) {
                const { backgroundColor = ActiveImageConstants.backgroundColor, fontColor = ActiveImageConstants.fontColor } =
                    this.props.config?.additionalContent || {};
                this.detailsRef.current.classList.add('vertical-align');
                this.detailsRef.current.style.background = backgroundColor;
                this.detailsRef.current.style.color = fontColor;
                if (this.overlayRef.current) {
                    this.overlayRef.current.style.display = 'none';
                }
            } else if (this.overlayRef.current) {
                const gradientColor = this.props.config.additionalContent?.gradientColor;
                this.overlayRef.current.style.background = gradientColor || '';
                this.overlayRef.current.style.display = 'block';
            }
        }
        this.setState({ clientWidth: this.getActiveImageWidth(), viewport, index: undefined, area: undefined, device });
        this.resetState();
    };

    private onNext = () => {
        const index = Math.min((this.state.index ?? 0) + 1, this._activePoints.length - 1);
        const area = this._activePoints[index];
        this.setState({ area, index });
    };
    private onPrev = () => {
        const index = Math.max((this.state.index ?? 0) - 1, 0);
        const area = this._activePoints[index];
        this.setState({ area, index });
    };

    private renderAllFeaturedProducts = () => {
        if (!this.props.config.activePoints?.length) {
            return null;
        }
        const { allFeaturedHeading, productImageSettings } = this.props.config;
        if (productImageSettings) {
            productImageSettings.lazyload = !productImageSettings.disableLazyLoad;
        }
        return (
            <>
                {this.state.isLoading ? (
                    <p className='isLoading'>Loading...</p>
                ) : (
                    <div className='ms-product-collection'>
                        {allFeaturedHeading && <h2 className='ms-product-collection__heading'>{allFeaturedHeading}</h2>}

                        <SingleSlideCarousel>
                            {this._activePoints.map((point, index) => {
                                const ProductContainer: INodeProps = {
                                    tag: 'li',
                                    className: 'ms-product-collection__item msc-active-shadow',
                                    role: 'listitem',
                                    key: index
                                };
                                const prodWithDelivery: IProductWithModes = {
                                    ...point.productSearchResult!,
                                    isCnC: false,
                                    isHomeDelivery: false,
                                    clubPlusBanner: false
                                };
                                return (
                                    <Node {...ProductContainer} key=''>
                                        <ProductComponent
                                            context={this.props.context}
                                            telemetryContent={this._telemetryContent}
                                            imageSettings={productImageSettings}
                                            id={this.props.id}
                                            typeName={this.props.typeName}
                                            data={{ product: prodWithDelivery }}
                                            discountLine={
                                                this.state.discountLines && this.state.discountLines.length > 0
                                                    ? this.state.discountLines[index]
                                                    : []
                                            }
                                            nonClubPlusDiscount={
                                                this.state.nonClubPlusDiscount?.filter(
                                                    item => item.ProductId === prodWithDelivery.RecordId
                                                )[0]
                                            }
                                            isNewCarouselDesign={this.props.config.isNewCarouselDesign}
                                        />
                                        {!this.props.config.isNewCarouselDesign ? (
                                            <a className='msc-add-to-carts' href={point.productUrl}>
                                                {this.props.resources.seeMoreButtonText}
                                            </a>
                                        ) : (
                                            <></>
                                        )}
                                    </Node>
                                );
                            })}
                        </SingleSlideCarousel>
                    </div>
                )}
            </>
        );
    };

    private renderActiveProductsCarousel = (): React.ReactNode => {
        if (this.state.index === undefined || !this.props.config.activePoints?.length) {
            return null;
        }
        return (
            <ActiveProductsCarousel
                activeIndex={this.state.index}
                products={this._activePoints.map(this.renderProductComponent)}
                next={this.onNext}
                prev={this.onPrev}
            />
        );
    };

    private renderAdditionalContent = () => {
        const { backgroundColor = ActiveImageConstants.backgroundColor, fontColor = ActiveImageConstants.fontColor } =
            this.props.config.additionalContent || {};
        const isDesktop = this.state.device === DeviceType.Desktop;
        const detailsColors = isDesktop ? {} : { background: backgroundColor, color: fontColor };
        const detailsContainer = {
            className: `msc-active-image__details ${isDesktop ? '' : 'vertical-align'}`,
            style: detailsColors,
            ref: this.detailsRef
        };
        const detailsContainerCta = { className: 'msc-active-image__details__cta' };
        const title = this.renderActiveImageDetailsHeading();
        const links = this.renderActiveImageDetailsLinks();
        const additionalContent = activeImageAdditionalContent({
            requestContext: this.props.context.request,
            additionalContent: { subtext: this.props.config.additionalContent?.subtext },
            handleAdditionalTextChange: this.handleAdditionalTextChange,
            handleAdditionalParagraphChange: this.handleAdditionalParagraphChange,
            handleAdditionalLinkTextChange: this.handleAdditionalLinkTextChange
        });
        if (!(title || links || additionalContent)) {
            return null;
        }
        return (
            <>
                <div className='additional-content-overlay' ref={this.overlayRef}></div>
                <Node {...detailsContainer} className={detailsContainer?.className || ''}>
                    {title}
                    {additionalContent && (
                        <Node {...additionalContent.additionalContentNode}>
                            <Node {...additionalContent.additionalContentContainer}>{additionalContent.text}</Node>
                        </Node>
                    )}
                    <Node {...detailsContainerCta} className={detailsContainerCta ? detailsContainerCta.className : ''}>
                        {links}
                    </Node>
                </Node>
            </>
        );
    };

    /**
     * Render method.
     * @returns - Returns JSX.Element | null.
     */
    public render(): JSX.Element | null {
        const { showAllFeatured } = this.props.config;
        const { device } = this.state;
        const viewProps: IActiveImageViewProps = {
            ...this.props,
            activePointContainer: {
                moduleProps: this.props,
                className: classnames('msc-active-image', this._activeImageClass, this.props.config.className)
            },
            activePointImageContainer: {
                tag: 'div',
                className: classnames('msc-active-image-wrapper', this._activeImageClass, this.props.config.className),
                ref: this._activeImageRefs
            },
            activePointDetailsContainer: {
                tag: 'div',
                className: classnames('msc-active-image-details-container', this._activeImageClass, this.props.config.className)
            },
            imgWidth: this.props.config.canvasWidth,
            imageMapper: this.renderImageMapper(),
            productCard: device === DeviceType.Mobile ? this.renderActiveProductsCarousel() : this.renderProductCard(),
            featuredProducts: showAllFeatured && this.renderAllFeaturedProducts(),
            additionalContent: this.renderAdditionalContent(),
            handleAdditionalText: this.handleAdditionalTextChange,
            handleAdditionalParagraph: this.handleAdditionalParagraphChange,
            handleAdditionalLinkText: this.handleAdditionalTextChange
        };

        return this.props.renderView(viewProps);
    }

    private reorderActivePoints() {
        const reOrderedActivePoints: IArea[] = [];
        if (this.props.config.activePoints) {
            for (const cap of this.props.config.activePoints) {
                for (const ap of this._activePoints) {
                    if (cap.itemId === ap.areaId) {
                        reOrderedActivePoints.push(ap);
                    }
                }
            }
            this._activePoints.splice(0, this._activePoints.length);
            reOrderedActivePoints.map(rap => {
                this._activePoints.push(rap);
            });
        }
    }

    private getAccumulatedAvailability = (
        ctx: MsDyn365.IActionContext,
        ProductWarehouseInventoryAvailabilities: ProductWarehouseInventoryAvailability[],
        productId: number
    ) => {
        // Just check inventory in warehouseIdsForFulfillment
        const {
            requestContext: {
                app: {
                    config: { warehouseIdsForFulfillment }
                }
            }
        } = ctx;

        let accumulatedProductCount = 0;
        const fulfillmentStores: string[] | undefined = warehouseIdsForFulfillment; // ['S011', 'S040']
        // We'll just check the PhysicalAvailable for plants fulfillment
        ProductWarehouseInventoryAvailabilities.filter(availability => {
            // First check if it is one of the warehouseIdsForFulfillment, only then add inventory
            return (
                fulfillmentStores &&
                fulfillmentStores.find(store => {
                    return availability.InventLocationId && availability.InventLocationId.toLowerCase() === store.toLowerCase();
                })
            );
        }).map(availability => {
            if (availability.PhysicalAvailable && availability.ProductId === productId) {
                if (availability.PhysicalAvailable && availability.PhysicalAvailable > 0) {
                    accumulatedProductCount += availability.PhysicalAvailable;
                }
            }
        });
        // Now just create an object with this product availability info and returns it
        return this.mapWarehouseAvailabilityForAccumulatedCount(
            ctx,
            ProductWarehouseInventoryAvailabilities.filter(item => item.ProductId === productId),
            accumulatedProductCount
        );
    };

    private mapWarehouseAvailabilityForAccumulatedCount = (
        ctx: MsDyn365.IActionContext,
        ProductWarehouseInventoryAvailabilities: ProductWarehouseInventoryAvailability[],
        accumulatedProductCount: number
    ): ProductWarehouseInventoryAvailability[] => {
        // Use labels from app.settings for both in stock & out of stock cases
        const {
            requestContext: {
                app: {
                    config: { inStockCode, inStockLabel, outOfStockCode, outOfStockLabel }
                }
            }
        } = ctx;

        const productAvailability = ProductWarehouseInventoryAvailabilities[0];

        return [
            {
                ProductId: productAvailability.ProductId,
                DataAreaId: productAvailability.DataAreaId,
                PhysicalInventory: 0,
                TotalAvailable: accumulatedProductCount,
                TotalAvailableInventoryLevelLabel: accumulatedProductCount > 0 ? inStockLabel : outOfStockCode,
                TotalAvailableInventoryLevelCode: accumulatedProductCount > 0 ? inStockCode : outOfStockLabel,
                PhysicalAvailable: accumulatedProductCount,
                PhysicalAvailableInventoryLevelLabel: accumulatedProductCount > 0 ? inStockLabel : outOfStockLabel,
                PhysicalAvailableInventoryLevelCode: accumulatedProductCount > 0 ? inStockCode : outOfStockCode
            }
        ];
    };

    private setupInventoryInformation = async (products: ProductSearchResult[]) => {
        let productInvInfo: IProductInventoryInformation[] = [];
        try {
            /* VSI Customization - START - 09/04/21 */
            // Check if product is vendorShipped we dont need getEstimatedAvailabilityAsync call as we'll just display instock with maxCartLineQty limit
            const {
                requestContext: {
                    app: {
                        config: { fulfillmentAttributeName, fulfillmentAttributeTextValue }
                    }
                }
            } = this.props.context.actionContext;

            const productsListWithKitScenarioHandled = await this.getProductsListWithKitProductsHandled(products);

            const plantProdIds = productsListWithKitScenarioHandled
                .filter(prod => {
                    const attributeName = fulfillmentAttributeName
                        ? fulfillmentAttributeName
                              .toString()
                              .toLowerCase()
                              .trim()
                        : 'fulfillmenttype';
                    const attributeTextValue = fulfillmentAttributeTextValue
                        ? fulfillmentAttributeTextValue
                              .toString()
                              .toLowerCase()
                              .trim()
                        : 'plants';

                    const fulfillmentTypeAttribute =
                        prod.AttributeValues &&
                        prod.AttributeValues.find(attribute => {
                            return attribute?.Name?.toLowerCase() === attributeName;
                        });
                    const isPlantFulfillment =
                        fulfillmentTypeAttribute && fulfillmentTypeAttribute.TextValue?.toLowerCase() === attributeTextValue;
                    const isPlantCategory = isPlantFulfillment ? true : false;
                    if (isPlantCategory) {
                        return true;
                    }
                    return false;
                })
                .map(p => p.RecordId);

            const nonPlantProductIds = productsListWithKitScenarioHandled
                .filter(prod => {
                    const attributeName = fulfillmentAttributeName
                        ? fulfillmentAttributeName
                              .toString()
                              .toLowerCase()
                              .trim()
                        : 'fulfillmenttype';
                    const attributeTextValue = fulfillmentAttributeTextValue
                        ? fulfillmentAttributeTextValue
                              .toString()
                              .toLowerCase()
                              .trim()
                        : 'plants';

                    const fulfillmentTypeAttribute =
                        prod.AttributeValues &&
                        prod.AttributeValues.find(attribute => {
                            return attribute?.Name?.toLowerCase() === attributeName;
                        });
                    const isPlantFulfillment =
                        fulfillmentTypeAttribute && fulfillmentTypeAttribute.TextValue?.toLowerCase() === attributeTextValue;
                    const isPlantCategory = isPlantFulfillment ? true : false;
                    if (!isPlantCategory) {
                        return true;
                    }
                    return false;
                })
                .map(p => p.RecordId);

            let plantsEstimatedAvailability = null;
            if (plantProdIds && plantProdIds.length > 0) {
                plantsEstimatedAvailability = await getEstimatedAvailabilityAsync(
                    { callerContext: this.props.context.actionContext },
                    {
                        ProductIds: plantProdIds,
                        DefaultWarehouseOnly: false,
                        FilterByChannelFulfillmentGroup: true
                    }
                );
            }

            let nonPlantsEstimatedAvailability = null;
            if (nonPlantProductIds && nonPlantProductIds.length > 0) {
                nonPlantsEstimatedAvailability = await getEstimatedAvailabilityAsync(
                    { callerContext: this.props.context.actionContext },
                    {
                        ProductIds: nonPlantProductIds,
                        DefaultWarehouseOnly: true,
                        FilterByChannelFulfillmentGroup: false
                    }
                );
            }
            let productAvailabilitesNonPlantsCNC = null;
            if (nonPlantProductIds && nonPlantProductIds.length) {
                productAvailabilitesNonPlantsCNC = await getEstimatedAvailabilityAsync(
                    { callerContext: this.props.context.actionContext },
                    {
                        ProductIds: nonPlantProductIds,
                        DefaultWarehouseOnly: false,
                        FilterByChannelFulfillmentGroup: true
                    }
                );
            }

            if (plantsEstimatedAvailability?.ProductWarehouseInventoryAvailabilities) {
                plantProdIds.map(productId => {
                    const accumulatedAvailability = this.getAccumulatedAvailability(
                        this.props.context.actionContext,
                        plantsEstimatedAvailability.ProductWarehouseInventoryAvailabilities || [],
                        productId
                    );

                    productInvInfo = productInvInfo.concat(
                        mapProductInventoryInformation(this.props.context.actionContext, accumulatedAvailability)
                    );
                });
            }

            if (nonPlantsEstimatedAvailability?.ProductWarehouseInventoryAvailabilities) {
                productInvInfo = productInvInfo.concat(
                    mapProductInventoryInformation(
                        this.props.context.actionContext,
                        nonPlantsEstimatedAvailability.ProductWarehouseInventoryAvailabilities
                    )
                );
            }
            const deliveryOptions = await getDeliveryOptionsAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                nonPlantProductIds ? nonPlantProductIds : []
            );
            if (productAvailabilitesNonPlantsCNC) {
                this.setState({
                    cncInventoryInfo: productAvailabilitesNonPlantsCNC
                });
            }

            this.setState({
                inventoryInfo: productInvInfo,
                // cncInventoryInfo: productAvailabilitesNonPlantsCNC,
                deliveryOptions: deliveryOptions,
                products: productsListWithKitScenarioHandled,
                isLoading: false
            });
        } catch (error) {
            this.props.context.actionContext.trace((error as Error).message);
            this.props.context.actionContext.telemetry.exception(error as Error);
            this.props.context.actionContext.telemetry.debug(`[getEstimatedAvailabilityAsync]Error executing action`);
            throw new Error('[getEstimatedAvailabilityAsync]Error executing action');
        }
    };
    private async getProductsListWithKitProductsHandled(products: ProductSearchResult[]): Promise<ProductSearchResult[]> {
        const finalProducts: ProductSearchResult[] = [];
        const variantIds: number[][] = [];
        if (products && products.length) {
            for (const p of products) {
                if (p) {
                    const variants = await getVariantsByDimensionValuesAsync(
                        { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        p.RecordId,
                        this.props.context.request.apiSettings.channelId,
                        []
                    );
                    if (variants && variants.length === 1) {
                        variantIds.push([p.RecordId, variants[0].RecordId]);
                        // variants[0].PrimaryImageUrl = p.PrimaryImageUrl;
                        // finalProducts.push(variants[0]);
                    } else {
                        finalProducts.push(p);
                    }
                }
            }
        }
        if (variantIds.length) {
            variantIds.map(variantObj => {
                if (Array.isArray(variantObj)) {
                    const prod = products.find(p => p.RecordId === variantObj[0]);
                    if (prod) {
                        prod.RecordId = variantObj[1];
                        finalProducts.push(prod);
                    }
                }
            });
        }

        return finalProducts;
    }
    private isProductAvailable = (productId: number): boolean => {
        const itemInventorys = this.state.inventoryInfo.filter(inventory => inventory.ProductAvailableQuantity.ProductId === productId);
        if (itemInventorys && itemInventorys.length) {
            for (const iv of itemInventorys) {
                if (iv && iv.IsProductAvailable) {
                    return true;
                }
            }
        }

        const prod = this.props.data.productsCarousel.result?.find(p => p.RecordId === productId);

        const isVendorShipAttribute = prod?.AttributeValues?.find(attribute => {
            const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
            return attributeName === 'isvendershipproduct';
        });

        if (isVendorShipAttribute && isVendorShipAttribute.TextValue && isVendorShipAttribute.TextValue.toLowerCase() === 'yes') {
            return true;
        }

        const onlyInStorAttribute = prod?.AttributeValues?.find(attribute => {
            const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
            return attributeName === 'onlyinstore';
        });

        if (onlyInStorAttribute && onlyInStorAttribute.TextValue && onlyInStorAttribute.TextValue.toLowerCase() === 'yes') {
            return true;
        }

        let isCnc = false;

        if (this.state.cncInventoryInfo && this.state.cncInventoryInfo.AggregatedProductInventoryAvailabilities) {
            this.state.cncInventoryInfo.AggregatedProductInventoryAvailabilities.map(stores => {
                if (stores.ProductId === productId && stores.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                    this.state.deliveryOptions.map(item => {
                        item.DeliveryOptions?.map(option => {
                            if (option.Code === 'ClickCol') {
                                isCnc = true;
                            }
                        });
                    });
                }
            });
        }
        if (isCnc) {
            return true;
        }
        return false;
    };

    /**
     * RenderImageMapper method - To render image mapper.
     * @returns - Returns React.ReactNode | null.
     */
    public renderImageMapper = (): React.ReactNode | null => {
        const { config, context } = this.props;
        const { device, area, index } = this.state;
        const defaultPosition: number = 0;
        const defaultLength: number = 0;
        const productResult = this.state.products; //this.props.data.productsCarousel.result;
        const filteredProducts = productResult?.filter(p => this.isProductAvailable(p.RecordId)); // productResult?.filter(x => x.AttributeValues?.some(y => y.TextValue === 'Out of stock'));
        const productResults = productResult?.filter(item => filteredProducts?.includes(item));
        if (config.activePoints && productResults) {
            for (const product of productResults) {
                for (const configProduct of config.activePoints) {
                    if (product.RecordId === Number(configProduct.itemId) || product.MasterProductId === Number(configProduct.itemId)) {
                        if (product.MasterProductId === Number(configProduct.itemId)) {
                            product.RecordId = Number(configProduct.itemId);
                        }
                        const productUrl = getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined);

                        const coords = [
                            configProduct.xPosition ?? defaultPosition,
                            configProduct.yPosition ?? defaultPosition,
                            configProduct.radius ?? defaultPosition
                        ];
                        const area = this._activePoints.find(activePoint => JSON.stringify(activePoint.coords) !== JSON.stringify(coords));
                        this.addActivePoints(area, product, coords, productUrl);
                    }
                }
            }
        }
        this.reorderActivePoints();
        const mappedArea: IAreaMap = {
            name: `map-${this._activeImageClass}`,
            areas: this._activePoints.length > defaultLength ? this._activePoints : []
        };
        const activeArea = device === DeviceType.Mobile ? (index === undefined ? undefined : this._activePoints[index]) : area;
        if (this.props.config.activeImage) {
            return (
                <ImageMapper
                    viewPort={this.state.viewport}
                    requestContext={this.props.context.actionContext.requestContext}
                    width={this.state.clientWidth}
                    imgWidth={this.props.config.canvasWidth}
                    onMouseEnter={this.enterArea}
                    onMouseLeave={this.leaveArea}
                    imageData={this.props.config.activeImage}
                    areaMap={mappedArea}
                    activeImageRefs={this._activeImageRefs}
                    imageMapperResources={{
                        altText: this.props.resources.altTextForSeeMoreButtonWithProductName,
                        buttonText: this.props.resources.seeMoreButtonText
                    }}
                    activeArea={activeArea}
                />
            );
        }

        return null;
    };

    /**
     * EnterArea method - To handle mouse enter event.
     * @param area - Active point area.
     * @param index - Active point index.
     * @param event - Active point AreaEvent.
     */
    public enterArea = (area: IArea, index?: number, event?: AreaEvent): void => {
        this.timeout && clearTimeout(this.timeout);
        this.timeout = undefined;
        this.setState({ area, index: index ?? this.state.index });
        if (event?.type.toLocaleLowerCase() === 'click') {
            window.open(area.productUrl, '_self');
        }
    };

    /**
     * LeaveArea method - To handle mouse leave event.
     */
    public leaveArea = (): void => {
        if (this.state.device !== DeviceType.Mobile) {
            this.timeout && clearTimeout(this.timeout);
            this.timeout = setTimeout(() => this.resetState(), ActiveImageConstants.leaveDelay);
        }
    };

    /**
     * ResetState method - To reset the state for area and overlayclass.
     */
    public resetState = (): void => {
        this.setState({ area: undefined, index: undefined });
    };

    /**
     * RenderProductComponent method - To render the product card with product information.
     * @returns - Returns React.ReactNode.
     */
    public renderProductComponent = (area?: IArea): React.ReactNode => {
        if (area?.productSearchResult) {
            const baseImageUrl: string = this.props.context.request.apiSettings.baseImageUrl;
            const imageUrl: string = area.primaryImageUrl ?? '';
            const primaryImageUrl: string = `${baseImageUrl}${imageUrl}`;
            const product: ProductSearchResult = area.productSearchResult;
            product.PrimaryImageUrl = primaryImageUrl;
            const { productImageSettings } = this.props.config;
            if (productImageSettings) {
                productImageSettings.lazyload = !productImageSettings.disableLazyLoad;
            }

            return (
                <ActiveProductComponent
                    context={this.props.context}
                    imageSettings={productImageSettings}
                    id={product.ItemId ?? ''}
                    typeName={this.props.typeName || ''}
                    data={{ product, url: area.productUrl }}
                    seeMoreButtonText={this.props.resources.seeMoreButtonText}
                />
            );
        }
        return React.Fragment;
    };

    /**
     * RenderProductCard method - To render product cards.
     * @returns - Returns React.ReactNode.
     */
    public renderProductCard = (): React.ReactNode => {
        const { index, area } = this.state;
        const productCardleftPosition = this.getProductCardPositionX();
        const productCardtopPosition = this.getProductCardPositionY();
        if (!area) {
            return null;
        }
        return (
            <Node
                className='msc-active-image-product-card-container'
                style={{ top: `${productCardtopPosition}px`, left: `${productCardleftPosition}px` }}
            >
                <div
                    className='msc-active-image-product-popup'
                    ref={this._productCardDivRef}
                    onMouseEnter={() => this.enterArea(area, index)}
                    onMouseLeave={this.leaveArea}
                    onFocus={() => this.enterArea(area, index)}
                    onBlur={this.leaveArea}
                    style={{ width: ActiveImageConstants.defaultPopupWidth }}
                >
                    <div className='msc-active-image-product-card-wrapper' key={index}>
                        {area.productSearchResult && this.renderProductComponent(area)}
                    </div>
                </div>
            </Node>
        );
    };

    /**
     * GetProductCardPositionX method - Provides product card x position to the left or right of active point based on available space.
     * For xs, sm and md viewports it provides x position for the center alignment.
     * @returns - Returns number.
     */
    public getProductCardPositionX = (): number => {
        const { area, clientWidth } = this.state;
        const { cardGap, defaultRadius, defaultPopupWidth } = ActiveImageConstants;
        const { activePoints } = this.props.config;
        const currentRadius =
            activePoints?.find(point => area?.center && point.xPosition === area.center[0] && point.yPosition === area.center[1])?.radius ||
            defaultRadius;
        const extraSpace = cardGap + currentRadius;
        const defaultPositionX: number = 0;
        const halfDivisor: number = 2;
        const productCardWidth = defaultPopupWidth;
        const pointPositionX = area?.center?.[0] ?? clientWidth / halfDivisor;
        const leftAllignPosition = pointPositionX - productCardWidth - extraSpace;
        const rightAllignPosition = pointPositionX + extraSpace;
        let position: number = (clientWidth - productCardWidth) / halfDivisor;
        if (leftAllignPosition < defaultPositionX) {
            position = rightAllignPosition;
        } else {
            position = leftAllignPosition;
        }
        return position;
    };

    /**
     * GetProductCardPositionY method - Provides product card y position in center with respect to active point.
     * For xs, sm and md viewports it provides y position for the center alignment.
     * @returns - Returns number.
     */
    public getProductCardPositionY = (): number => {
        const defaultImageSettings = this.getDefaultImageSettings();
        const productImageSettingsHeight = this.props.config.productImageSettings?.viewports
            ? this.props.config.productImageSettings.viewports.lg?.h
            : defaultImageSettings.viewports.lg?.h;
        const halfDivisor: number = 2;
        const { area } = this.state;
        const produtCardHight: number = productImageSettingsHeight ? productImageSettingsHeight : this._defaultProdutCardHight;
        const pointPositionY = (this.getActiveImageHeight() - produtCardHight) / halfDivisor;
        let position: number = pointPositionY;
        position = (area?.center?.[1] ?? this.getActiveImageHeight() / halfDivisor) - produtCardHight / halfDivisor;
        return position;
    };

    /**
     * GetDefaultImageSettings method.
     * @returns MsDyn365.IImageSettings.
     */
    public getDefaultImageSettings = (): MsDyn365.IImageSettings => {
        const defaultImageSettings: MsDyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                lg: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                xl: { q: 'w=275&h=275&m=6', w: 275, h: 275 }
            },
            lazyload: true
        };

        return defaultImageSettings;
    };

    /**
     * KeyDown method - To handle keydown event.
     * @param event - KeyboardEvent.
     */
    public keyDown = (event: KeyboardEvent): void => {
        if (event.key === ActiveImageConstants.enterKey) {
            const target: HTMLElement = event.target as HTMLElement;
            if (target.nodeName === 'AREA') {
                const area: HTMLAreaElement = target as HTMLAreaElement;
                if (area.href) {
                    window.open(area.href, '_self');
                }
            }
        }
        if (event.key === ActiveImageConstants.escKey) {
            this.resetState();
        }
    };

    /**
     * GetActiveImageWidth method - To get active image width.
     * @returns Returns number.
     */
    public getActiveImageWidth = (): number => {
        const defaultWidth: number = 0;
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            const activeImageReferenceValue = this._activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientWidth;
            }
        }
        return defaultWidth;
    };

    /**
     * GetActiveImageHeight method - To get active image height.
     * @returns Returns number.
     */
    public getActiveImageHeight = (): number => {
        const defaultHeight: number = 0;
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            const activeImageReferenceValue = this._activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientHeight;
            }
        }
        return defaultHeight;
    };

    /**
     * HandleLinkTextChange method - To handle link text change event.
     * @param linkIndex - The link index.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleLinkTextChange(linkIndex: number, event: MsDyn365.ContentEditableEvent): void {}

    /**
     * HandleTextChange method - To handle text change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: MsDyn365.ContentEditableEvent): void => {};

    /**
     * HandleParagraphChange method - To handle paragraph change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: MsDyn365.ContentEditableEvent): void => {};

    /**
     * HandleAdditionalTextChange method - To handle additional text change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalTextChange(event: MsDyn365.ContentEditableEvent): void {}

    /**
     * HandleAdditionalLinkTextChange method - To handle additional link text change event.
     * @param linkIndex - The link index.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalLinkTextChange(linkIndex: number, event: MsDyn365.ContentEditableEvent): void {
        const additionalContent = this.props.config.additionalContent ?? {};
        if (additionalContent.links) {
            additionalContent.links[Number(linkIndex)].linkText = event.target.value;
        }
    }

    /**
     * HandleAdditionalParagraphChange method - To handle additional paragraph change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalParagraphChange(event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.additionalContent) {
            this.props.config.additionalContent.subtext = event.target.value;
        }
    }

    /**
     * RenderActiveImageDetailsLinks method - To render active image details links.
     * @returns - React node.
     */
    public renderActiveImageDetailsLinks = (): React.ReactNode[] | undefined => {
        const links = this.props.config.additionalContent?.links;
        if (!links?.length) {
            return undefined;
        }
        const payLoad = getPayloadObject('click', this._telemetryContent, '');

        return links?.map((linkData: IActiveImageLinksData, index: number) => {
            payLoad.contentAction.etext = linkData.linkText;
            const attributes = getTelemetryAttributes(this._telemetryContent, payLoad);
            const linkId = index;
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: linkData.ariaLabel,
                className: 'msc-active-image__details__cta__link',
                linkText: linkData.linkText,
                linkUrl: linkData.linkUrl.destinationUrl,
                openInNewTab: linkData.willOpenInNewTab,
                role: 'button',
                additionalProperties: attributes
            };

            return (
                <MsDyn365.Link
                    key={linkId}
                    link={editableLink}
                    editProps={{
                        /**
                         * Handle additional paragraph change.
                         * @param event - The keyboard event.
                         */
                        onTextChange: event => {
                            this.handleLinkTextChange(index, event);
                        },
                        requestContext: this.props.context.request
                    }}
                />
            );
        });
    };

    /**
     * RenderActiveImageDetailsHeading method - To render active image details heading.
     * @returns - React.ReactNode | undefined.
     */
    public renderActiveImageDetailsHeading = (): React.ReactNode | undefined => {
        const heading = this.props.config.additionalContent?.heading;
        const text = heading?.text;

        return (
            heading &&
            text && (
                <MsDyn365.Text
                    className='msc-active-image__details__title'
                    tag={heading.tag ?? 'h1'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
                />
            )
        );
    };

    /**
     * GetActiveImageId - To get active image id from src.
     * @returns - String.
     */
    public getActiveImageId = (): string => {
        return this.props.id;
    };

    /**
     * Handle outside click method - To handle outside click of the active image product.
     * @param event - Mouse event.
     */
    private readonly _handleOutsideClick = (event: MouseEvent) => {
        if (this._productCardDivRef.current?.contains(event.target as Node)) {
            return;
        }
        const { device } = this.state;
        device !== DeviceType.Mobile && this.resetState();
    };
}
export default ActiveImage;
